import { useMemo, useState } from "react";
import { type Sesion, crearCliente, guardarSesion, sesionGuardada } from "./api.ts";
import { C, SERIF } from "./theme.ts";
import { useRuta, rutaAHash } from "./rutas.ts";
import { Login } from "./pages/Login.tsx";
import { Panorama } from "./pages/Panorama.tsx";
import { Explorador } from "./pages/Explorador.tsx";
import { Ficha } from "./pages/Ficha.tsx";
import { FichaRegistro } from "./pages/FichaRegistro.tsx";
import { Red } from "./pages/Red.tsx";
import { Cruce } from "./pages/Cruce.tsx";
import { Documentos } from "./pages/Documentos.tsx";
import { Justicia } from "./pages/Justicia.tsx";
import { RevisarDocumento } from "./pages/RevisarDocumento.tsx";

export default function App() {
  const [sesion, setSesion] = useState<Sesion | null>(sesionGuardada);
  const [ruta] = useRuta();

  const cliente = useMemo(() => {
    if (!sesion) return null;
    return crearCliente(
      sesion,
      (s) => {
        guardarSesion(s);
      },
      () => {
        guardarSesion(null);
        setSesion(null);
      },
    );
  }, [sesion]);

  if (!sesion || !cliente) {
    return (
      <Login
        onSesion={(s) => {
          guardarSesion(s);
          setSesion(s);
        }}
      />
    );
  }

  const tabs = [
    { vista: "panorama" as const, etiqueta: "Panorama" },
    { vista: "explorador" as const, etiqueta: "Explorador" },
    { vista: "cruce" as const, etiqueta: "Cruce de listas" },
    { vista: "documentos" as const, etiqueta: "Documentos" },
    { vista: "justicia" as const, etiqueta: "Justicia" },
  ];
  const vistaActiva = ruta.vista === "entidad" || ruta.vista === "red" || ruta.vista === "registro" || ruta.vista === "representante"
    ? "explorador"
    : ruta.vista === "documento" || ruta.vista === "revisar"
    ? "documentos"
    : ruta.vista;

  return (
    <div style={{ minHeight: "100vh" }}>
      <header
        style={{
          background: C.tielDark,
          color: "#fff",
          padding: "0 22px",
          display: "flex",
          alignItems: "center",
          gap: 26,
          height: 56,
        }}
      >
        <a
          href={rutaAHash({ vista: "panorama" })}
          style={{ display: "flex", alignItems: "baseline", gap: 8, textDecoration: "none" }}
        >
          <span style={{ fontFamily: SERIF, fontSize: 20, letterSpacing: "0.12em", color: "#fff" }}>ARGUS</span>
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.24em", color: C.oro, textTransform: "uppercase" }}>
            Nexo
          </span>
        </a>
        <nav style={{ display: "flex", gap: 4, flex: 1 }}>
          {tabs.map((t) => {
            const activa = vistaActiva === t.vista;
            return (
              <a
                key={t.vista}
                href={rutaAHash({ vista: t.vista })}
                style={{
                  padding: "6px 12px",
                  borderRadius: 8,
                  fontSize: 13.5,
                  fontWeight: 600,
                  textDecoration: "none",
                  color: activa ? "#fff" : "#B8CDCB",
                  background: activa ? "rgba(255,255,255,0.12)" : "transparent",
                  borderBottom: activa ? `2px solid ${C.oro}` : "2px solid transparent",
                }}
              >
                {t.etiqueta}
              </a>
            );
          })}
          <a href="/quality-review.html" style={{ padding: "6px 12px", color: "#B8CDCB", textDecoration: "none", fontSize: 13.5, fontWeight: 600 }}>Calidad IA</a>
        </nav>
        <span style={{ fontSize: 12, color: "#B8CDCB" }}>{sesion.email}</span>
        <button
          style={{
            background: "transparent",
            border: "1px solid rgba(255,255,255,0.3)",
            color: "#fff",
            borderRadius: 8,
            padding: "5px 12px",
            fontSize: 12.5,
            cursor: "pointer",
          }}
          onClick={() => {
            guardarSesion(null);
            setSesion(null);
          }}
        >
          Salir
        </button>
      </header>

      <main style={{ maxWidth: 1200, margin: "0 auto", padding: "18px 22px 60px" }}>
        {ruta.vista === "panorama" ? <Panorama cliente={cliente} /> : null}
        {ruta.vista === "explorador" ? <Explorador cliente={cliente} /> : null}
        {ruta.vista === "entidad" ? <Ficha cliente={cliente} id={ruta.id} actor={ruta.actor} /> : null}
        {ruta.vista === "registro" ? <FichaRegistro key={`${ruta.runId}/${ruta.id}`} cliente={cliente} sujeto={{ kind: "siger_actor", id: ruta.id, runId: ruta.runId }} /> : null}
        {ruta.vista === "representante" ? <FichaRegistro key={`sat_rep/${ruta.id}`} cliente={cliente} sujeto={{ kind: "sat_rep", id: ruta.id }} /> : null}
        {ruta.vista === "red" ? <Red cliente={cliente} id={ruta.id} saltos={ruta.saltos} /> : null}
        {ruta.vista === "cruce" ? <Cruce cliente={cliente} /> : null}
        {ruta.vista === "documentos" ? <Documentos cliente={cliente} /> : null}
        {ruta.vista === "documento" ? <Documentos cliente={cliente} id={ruta.id} /> : null}
        {ruta.vista === "revisar" ? <RevisarDocumento cliente={cliente} id={ruta.id} /> : null}
        {ruta.vista === "justicia" ? <Justicia cliente={cliente} /> : null}
      </main>
    </div>
  );
}
